'use client';

import * as DS from '@aurorasolar/ds';
import Image, { StaticImageData } from 'next/image';
import { ReactNode } from 'react';

import { bgDots, bgDotsDark } from '../assets';

type HeroImageProps = { alt: string; maxWidth?: number; src: StaticImageData; verticalAlign?: 'center' | 'end' };

type Props = {
  bgDotsOverlay?: boolean;
  bgImg?: StaticImageData;
  children: ReactNode;
  colorMode?: 'light' | 'dark';
  headline: string;
  heroImage?: HeroImageProps;
  horizontalAlignment?: 'center' | 'start';
  smallMobileHeadline?: boolean;
  subheadline?: string;
};

export const PageHero = ({
  bgDotsOverlay,
  bgImg,
  children,
  colorMode = 'dark',
  headline,
  heroImage,
  horizontalAlignment = 'center',
  smallMobileHeadline = false,
  subheadline,
}: Props) => {
  const ModeProvider = colorMode === 'dark' ? DS.DarkModeProvider : DS.LightModeProvider;
  const bgBrightness = colorMode === 'dark' ? 40 : 100;
  const bgOverlay = colorMode === 'dark' ? bgDotsDark : bgDots;

  return (
    <ModeProvider>
      <DS.Flex
        flexDirection="column"
        justifyContent="center"
        minH={heroImage ? { _: 'auto', lg: '100dvh' } : '100dvh'}
        mt={-60}
        pb={heroImage ? 0 : 6}
        position="relative"
        px={6}
      >
        <>
          {bgImg && (
            <Image
              alt=""
              fill
              priority
              sizes="100vw"
              src={bgImg}
              style={{ filter: `brightness(${bgBrightness}%)`, objectFit: 'cover' }}
            />
          )}
          {bgDotsOverlay && <Image alt="" fill sizes="100vw" src={bgOverlay} style={{ objectFit: 'cover' }} />}
        </>

        <DS.Flex
          alignItems={horizontalAlignment}
          flex={1}
          gap={4}
          marginLeft={horizontalAlignment === 'start' ? { _: 0, lg: 9 } : undefined}
          mx={horizontalAlignment === 'center' ? 'auto' : undefined}
          position="relative"
          textAlign={horizontalAlignment}
          w={{ _: '100%', lg: '80%' }}
          zIndex={1}
        >
          <DS.Flex alignItems={horizontalAlignment} flex={1} gap={4} justifyContent="end">
            <DS.Text
              as="h1"
              fontSize={smallMobileHeadline ? { _: '44px' } : undefined}
              maxW={{ _: '700px', lg: '900px' }}
              pt={10}
              text={{ _: smallMobileHeadline ? 'd3' : 'd1', lg: 'd3', md: 'd2' }}
            >
              {headline}
            </DS.Text>
            {subheadline ? (
              <DS.Text text={{ _: 'body16', md: 'body20' }} textAlign={horizontalAlignment} w="80%">
                {subheadline}
              </DS.Text>
            ) : null}
          </DS.Flex>
          <DS.Flex alignItems={horizontalAlignment} flex={1} justifyContent="space-between">
            {children}
          </DS.Flex>
        </DS.Flex>
        {heroImage && (
          <DS.Flex
            alignItems={horizontalAlignment}
            flexGrow={1}
            justifyContent={heroImage.verticalAlign ?? 'center'}
            position="relative"
            pt={6}
            zIndex={1}
          >
            <Image
              alt={heroImage.alt}
              src={heroImage.src}
              style={{
                height: 'auto',
                maxWidth: heroImage.maxWidth ?? '100%',
                ...(heroImage.maxWidth ? { width: '100%' } : {}),
              }}
            />
          </DS.Flex>
        )}
      </DS.Flex>
    </ModeProvider>
  );
};
